import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Alert,
  Spin,
  Button,
  Modal,
  Input,
  Select,
  Checkbox,
  Space,
  Popover,
  Tag,
  message,
  Switch,
} from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getAllUsersAndAccounts,
  updateUser,
  deleteUser,
  getValidateLifetimeJwt,
  toggleValidateLifetimeJwt,
} from '../../services/users';
import type { UpdateUser } from '../../services/users';
import CreateUser from './CreateUser';
import type { User, Account } from '../../types/user';
import { toast } from 'sonner';
import { useAuth } from 'src/AuthContext';
import { ROLES } from 'src/constants/authConstants';

interface EditedUser {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  isDev?: boolean;
  liveShare?: boolean;
  accountIds?: string[];
}

type EditedUsers = Record<string, EditedUser>;

const thStyle: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontWeight: 500,
  borderBottom: '1px solid #d2d7dd',
  whiteSpace: 'nowrap',
  backgroundColor: '#DCE2E9',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderBottom: '1px dashed #d3d8dd',
  verticalAlign: 'middle',
};

const inputStyle: React.CSSProperties = {
  padding: 0,
  width: '100%',
};

interface UserRowProps {
  user: User;
  editedUser: EditedUser | undefined;
  accounts: Account[];
  isChanged: boolean;
  deleteDisabled: boolean;
  onUpdateField: (email: string, field: keyof EditedUser, value: any) => void;
  onToggleAccount: (email: string, accountId: string, checked: boolean) => void;
  onDelete: (user: User) => void;
  hasAccess: boolean;
}

const UserRow = memo(function UserRow({
  user: u,
  editedUser,
  accounts,
  isChanged,
  deleteDisabled,
  onUpdateField,
  onToggleAccount,
  onDelete,
  hasAccess,
}: UserRowProps) {
  const getValue = <T extends keyof User>(field: T): User[T] => {
    if (editedUser && field in editedUser) {
      return editedUser[field as keyof EditedUser] as User[T];
    }
    return u[field];
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);

    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };
  const userAccountIds = editedUser?.accountIds ?? u.accountsAccess?.map((a) => a.accountId) ?? [];
  const firstName = getValue('firstName') || '';
  const lastName = getValue('lastName') || '';

  return (
    <tr style={{ background: isChanged ? '#fffbe6' : 'transparent' }}>
      <td style={tdStyle}>{u.email}</td>
      <td style={tdStyle}>
        <Input
          size='small'
          variant='borderless'
          value={firstName}
          onChange={(e) => onUpdateField(u.email, 'firstName', e.target.value)}
          style={{
            ...inputStyle,
            ...(firstName.trim() === '' ? { outline: '1px solid #ff4d4f', borderRadius: 4 } : {}),
          }}
        />
      </td>
      <td style={tdStyle}>
        <Input
          size='small'
          variant='borderless'
          value={lastName}
          onChange={(e) => onUpdateField(u.email, 'lastName', e.target.value)}
          style={{
            ...inputStyle,
            ...(lastName.trim() === '' ? { outline: '1px solid #ff4d4f', borderRadius: 4 } : {}),
          }}
        />
      </td>
      <td style={tdStyle}>
        <Input
          size='small'
          variant='borderless'
          value={getValue('phone') || ''}
          onChange={(e) => onUpdateField(u.email, 'phone', e.target.value)}
          style={inputStyle}
        />
      </td>

      {/* Conditional Columns start here */}
      {hasAccess && (
        <>
          <td style={tdStyle}>
            <Select
              size='small'
              variant='borderless'
              value={getValue('role')}
              onChange={(value) => onUpdateField(u.email, 'role', value)}
              style={{ width: '100%' }}
            >
              {Object.entries(ROLES).map(([key, label]) => (
                <Select.Option
                  key={key}
                  value={key}
                  disabled={key === 'superadmin' && u.role !== 'superadmin'}
                >
                  {label}
                </Select.Option>
              ))}
            </Select>
          </td>
          <td style={{ ...tdStyle, textAlign: 'center' }}>
            <Checkbox
              checked={!!getValue('isDev')}
              onChange={(e) => onUpdateField(u.email, 'isDev', e.target.checked)}
            />
          </td>
          <td style={{ ...tdStyle, textAlign: 'center' }}>
            <Checkbox
              checked={!!getValue('liveShare')}
              onChange={(e) => onUpdateField(u.email, 'liveShare', e.target.checked)}
            />
          </td>
          <td style={tdStyle}>
            <Popover
              trigger='click'
              placement='bottomLeft'
              content={
                <div style={{ maxHeight: 400, overflowY: 'auto', minWidth: 200 }}>
                  {accounts.map((account) => {
                    const isChecked = userAccountIds.includes(account.accountId);
                    const isLastAccount = isChecked && userAccountIds.length === 1;
                    return (
                      <div key={account.accountId} style={{ padding: '4px 0' }}>
                        <Checkbox
                          checked={isChecked}
                          disabled={isLastAccount}
                          onChange={(e) =>
                            onToggleAccount(u.email, account.accountId, e.target.checked)
                          }
                        >
                          {account.accountName}
                        </Checkbox>
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div style={{ cursor: 'pointer' }}>
                {userAccountIds.length > 0 ? (
                  <Space size={[2, 2]} wrap>
                    {userAccountIds.slice(0, 2).map((id) => {
                      const account = accounts.find((a) => a.accountId === id);
                      return (
                        <Tag key={id} style={{ margin: 0, fontSize: 12, borderRadius: 12 }}>
                          {account?.accountName || id}
                        </Tag>
                      );
                    })}
                    {userAccountIds.length > 2 && (
                      <Tag style={{ margin: 0, fontSize: 12, borderRadius: 12 }}>
                        +{userAccountIds.length - 2}
                      </Tag>
                    )}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>None</span>
                )}
              </div>
            </Popover>
          </td>
        </>
      )}
      <td style={tdStyle}>{formatDateTime(u.createdAt)}</td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <Button
          type='text'
          size='small'
          danger
          disabled={deleteDisabled}
          icon={<DeleteOutlined />}
          onClick={() => onDelete(u)}
        />
      </td>
    </tr>
  );
});

const UserManagement: React.FC = () => {
  const [dpUsers, setDpUsers] = useState<User[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [validateLifetime, setValidateLifetime] = useState<boolean>(false);
  const [togglingLifetime, setTogglingLifetime] = useState<boolean>(false);
  type SortDirection = 'asc' | 'desc' | null;
  const [createdAtSort, setCreatedAtSort] = useState<SortDirection>(null);
  // Handle the toggle action
  const handleToggleLifetime = async (checked: boolean) => {
    setTogglingLifetime(true);
    try {
      await toggleValidateLifetimeJwt(checked);
      const val = await getValidateLifetimeJwt();
      setValidateLifetime(val);
      message.success(`Validate Lifetime JWT ${val ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to update setting';
      message.error(errorMsg);
    } finally {
      setTogglingLifetime(false);
    }
  };

  // Table state
  const [editedUsers, setEditedUsers] = useState<EditedUsers>({});
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { user } = useAuth();
  const hasAccess = ['shmuelt', 'yitzn', 'davidp', 'saras', 'devorahs', 'jennifert', 'ajay', 'nevil'].some((prefix) =>
    user.email.startsWith(prefix),
  );
  const isLimitedUserManager = user.role === 'user-manager';

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsersAndAccounts();
      const isLimited = !hasAccess && isLimitedUserManager;

      if (isLimited) {
        setDpUsers(data.dpUsers.filter((u) => u.accountId === user.accountId));
        setAccounts(data.accounts.filter((u) => u.accountId === user.accountId));
        setUsers(data.users.filter((u) => u.accountId === user.accountId));
      } else {
        setDpUsers(data.dpUsers);
        setAccounts(data.accounts);
        setUsers(data.users);
      }
    } catch (err) {
      setError('Failed to load users and accounts data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const val = await getValidateLifetimeJwt();
        setValidateLifetime(val);
      } catch (err) {
        console.error('Failed to fetch JWT settings', err);
      }
    };
    fetchData();
    fetchSettings();
  }, []);

  const handleUserCreated = () => {
    fetchData();
    setCreateModalOpen(false);
    toast.success('User created successfully!');
  };

  // Table logic
  const filteredUsers = useMemo(() => {
    let result = users;
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (u) =>
          u.email.toLowerCase().includes(lower) ||
          u.firstName?.toLowerCase().includes(lower) ||
          u.lastName?.toLowerCase().includes(lower),
      );
    }

    if (createdAtSort) {
      result = [...result].sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();

        return createdAtSort === 'asc' ? aTime - bTime : bTime - aTime;
      });
    }
    return result;
  }, [users, searchText, createdAtSort]);

  const updateField = useCallback(
    (email: string, field: keyof EditedUser, value: any) => {
      setEditedUsers((prev) => {
        const currentUser = users.find((x) => x.email === email);
        if (!currentUser) return prev;

        const originalAccountIds = currentUser.accountsAccess?.map((a) => a.accountId) || [];
        const isChanged =
          field === 'accountIds'
            ? [...originalAccountIds].sort().join(',') !== [...(value as string[])].sort().join(',')
            : currentUser[field as keyof User] !== value;

        const userEdits = { ...prev[email] };
        if (isChanged) {
          userEdits[field] = value;
        } else {
          delete userEdits[field];
        }

        if (Object.keys(userEdits).length === 0) {
          const next = { ...prev };
          delete next[email];
          return next;
        }
        return { ...prev, [email]: userEdits };
      });
    },
    [users],
  );

  const toggleAccount = useCallback(
    (email: string, accountId: string, checked: boolean) => {
      setEditedUsers((prev) => {
        const currentUser = users.find((x) => x.email === email);
        if (!currentUser) return prev;

        const currentEdited = prev[email];
        const currentIds =
          currentEdited?.accountIds ?? currentUser.accountsAccess?.map((a) => a.accountId) ?? [];

        // Prevent removing the last account
        if (!checked && currentIds.length === 1) return prev;

        const newIds = checked
          ? [...currentIds, accountId]
          : currentIds.filter((id: string) => id !== accountId);

        const originalIds = currentUser.accountsAccess?.map((a) => a.accountId) || [];
        const isChanged = [...originalIds].sort().join(',') !== [...newIds].sort().join(',');

        const userEdits = { ...prev[email] };
        if (isChanged) {
          userEdits.accountIds = newIds;
        } else {
          delete userEdits.accountIds;
        }

        if (Object.keys(userEdits).length === 0) {
          const next = { ...prev };
          delete next[email];
          return next;
        }
        return { ...prev, [email]: userEdits };
      });
    },
    [users],
  );

  const changedEmailsSet = useMemo(() => {
    const set = new Set<string>();
    for (const email of Object.keys(editedUsers)) {
      if (Object.keys(editedUsers[email]).length > 0) {
        set.add(email);
      }
    }
    return set;
  }, [editedUsers]);

  const hasAnyChanges = changedEmailsSet.size > 0;

  const cancelAllChanges = () => {
    setEditedUsers({});
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      await deleteUser(deleteConfirm.email);
      message.success('User deleted');
      setDeleteConfirm(null);
      // Remove from edited state if present
      setEditedUsers((prev) => {
        const next = { ...prev };
        delete next[deleteConfirm.email];
        return next;
      });
      fetchData();
    } catch {
      message.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const saveAllChanges = async () => {
    if (changedEmailsSet.size === 0) return;

    const emailsToSave = Array.from(changedEmailsSet);

    // Validate - block save if any user has empty first/last name
    for (const email of emailsToSave) {
      const currentUser = users.find((u) => u.email === email);
      if (!currentUser) continue;

      const edited = editedUsers[email];
      const firstName = edited?.firstName ?? currentUser.firstName;
      const lastName = edited?.lastName ?? currentUser.lastName;

      if (!firstName?.trim() || !lastName?.trim()) return;
    }

    setSaving(true);
    try {
      const updates: UpdateUser[] = emailsToSave.map((email) => {
        const edited = editedUsers[email];
        const current = users.find((u) => u.email === email)!;

        const update: UpdateUser = { email };
        if (edited.firstName !== undefined) update.firstName = edited.firstName;
        if (edited.lastName !== undefined) update.lastName = edited.lastName;
        if (edited.phone !== undefined) update.phone = edited.phone;
        if (edited.role !== undefined) update.role = edited.role as UpdateUser['role'];
        if (edited.isDev !== undefined) update.isDev = edited.isDev;
        if (edited.liveShare !== undefined) update.liveShare = edited.liveShare;

        if (edited.accountIds !== undefined) {
          update.accountIds = edited.accountIds;
        } else if (edited.role !== undefined) {
          update.accountIds = current.accountsAccess?.map((a) => a.accountId) ?? [];
        }

        return update;
      });

      const results = await Promise.allSettled(updates.map((u) => updateUser(u)));
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (failed > 0) {
        message.warning(`${succeeded} saved, ${failed} failed`);
      } else {
        message.success(`${succeeded} user(s) saved`);
      }

      setEditedUsers((prev) => {
        const next = { ...prev };
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            delete next[emailsToSave[idx]];
          }
        });
        return next;
      });

      fetchData();
    } catch {
      message.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  const canSeePage = hasAccess || user.role === 'user-manager';
  if (!canSeePage) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message='Access Denied'
          description='You do not have permission to view this page.'
          type='error'
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <Spin size='large' />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message={error} type='error' showIcon />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '24px',
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: 'calc(100vw - 200px)',
        width: 'calc(100vw - 200px)',
        margin: '0 auto',
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h2 style={{ margin: 0, lineHeight: '40px' }}>User Management</h2>
          <Input
            placeholder='Search...'
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={() => setSearchText(searchInput)}
            onKeyDown={(e) => e.key === 'Enter' && setSearchText(searchInput)}
            allowClear
            onClear={() => {
              setSearchInput('');
              setSearchText('');
            }}
            style={{ width: 300 }}
          />
        </div>

        <Space size='large'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Validate JWT Lifetime:</span>
            <Switch
              checked={validateLifetime}
              loading={togglingLifetime}
              onChange={handleToggleLifetime}
              checkedChildren='ON'
              unCheckedChildren='OFF'
            />
          </div>
          <Button type='primary' icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            Create User
          </Button>
        </Space>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          minHeight: 0,
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: 1000,
            borderCollapse: 'collapse',
            fontSize: 14,
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr style={{ background: '#fafafa', position: 'sticky', top: 0, zIndex: 1 }}>
              <th style={{ ...thStyle, width: '21%' }}>Email</th>
              <th style={{ ...thStyle, width: '10%' }}>First Name</th>
              <th style={{ ...thStyle, width: '10%' }}>Last Name</th>
              <th style={{ ...thStyle, width: '10%' }}>Phone</th>
              {hasAccess && <th style={{ ...thStyle, width: '10%' }}>Role</th>}
              {hasAccess && <th style={{ ...thStyle, width: '4%', textAlign: 'center' }}>Dev</th>}
              {hasAccess && (
                <th style={{ ...thStyle, width: '6%', textAlign: 'center' }}>Live sharing</th>
              )}
              {hasAccess && <th style={{ ...thStyle, width: '20%' }}>Accounts</th>}
              <th
                style={{ ...thStyle, width: '11%', cursor: 'pointer', userSelect: 'none' }}
                onClick={() =>
                  setCreatedAtSort((prev) =>
                    prev === 'asc' ? 'desc' : prev === 'desc' ? 'asc' : 'asc',
                  )
                }
              >
                Created Date {createdAtSort === null && '↑↓'}
                {createdAtSort === 'asc' && '↑'}
                {createdAtSort === 'desc' && '↓'}
              </th>
              <th style={{ ...thStyle, width: '4%' }} />
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <UserRow
                key={u.email}
                user={u}
                editedUser={editedUsers[u.email]}
                accounts={accounts}
                isChanged={changedEmailsSet.has(u.email)}
                deleteDisabled={hasAnyChanges}
                onUpdateField={updateField}
                onToggleAccount={toggleAccount}
                onDelete={setDeleteConfirm}
                hasAccess={hasAccess}
              />
            ))}
          </tbody>
        </table>
      </div>

      {hasAnyChanges && (
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            background: '#fff',
            borderRadius: 6,
            border: '1px solid #e8e8e8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#666' }}>
            {changedEmailsSet.size} unsaved change{changedEmailsSet.size > 1 ? 's' : ''}
          </span>
          <Space>
            <Button onClick={cancelAllChanges} disabled={saving}>
              Cancel
            </Button>
            <Button type='primary' onClick={saveAllChanges} loading={saving}>
              Save All
            </Button>
          </Space>
        </div>
      )}

      <Modal
        title='Create User'
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        width={700}
        destroyOnHidden
        centered
        styles={{ body: { maxHeight: '90vh', overflowY: 'auto' } }}
      >
        <CreateUser
          dpUsers={dpUsers}
          accounts={accounts}
          existingUsers={users}
          onUserCreated={handleUserCreated}
          hasAccess={hasAccess}
          currentAccountId={user.accountId}
          currentAccountName={user.accountName}
        />
      </Modal>

      <Modal
        title='Delete User'
        open={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onOk={handleDeleteUser}
        okText='Delete'
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        centered
      >
        {deleteConfirm && (
          <p>
            Are you sure you want to delete{' '}
            <strong>
              {deleteConfirm.firstName} {deleteConfirm.lastName}
            </strong>{' '}
            ({deleteConfirm.email})?
          </p>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
